import { FC, useState } from 'react';
import {
  LabelList,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { MonthlyGroupedRow } from '../threeMonth.types';
import { FORECASTING_THEME } from '../forecastingTheme';
import { formatCurrency, formatCurrencyCompact } from '../utils/forecastingAggregations';
import { ChartPanel, ChartPlot, ChartTitle, Legend, LegendButton } from '../Forecasting.styles';
import { currencyAxisProps, maxOf } from '../utils/currencyAxis';
import { useShellPalette } from '@/styles';

const SERIES = [
  { key: 'BookedPlusPipe', label: 'Booked + Pipe', color: FORECASTING_THEME.booked },
  { key: 'DynamicForecast', label: 'Dynamic Forecast', color: FORECASTING_THEME.forecast },
  { key: 'Target', label: 'Target', color: FORECASTING_THEME.pipe }
] as const;

type SeriesKey = (typeof SERIES)[number]['key'];

interface MonthlyGroupedChartProps {
  data: MonthlyGroupedRow[];
  title?: string;
  /** `YYYY-MM` of the month the page is narrowed to, if any. */
  selectedMonth?: string | null;
  /** Called with a month's key when its column is clicked. */
  onMonthClick?: (monthKey: string) => void;
}

/* Months other than the selected one stay visible but recede, so the quarter
   is still readable while the page shows a single month. */
const DIMMED_OPACITY = 0.3;

export const MonthlyGroupedChart: FC<MonthlyGroupedChartProps> = ({
  data,
  title = 'Monthly comparison',
  selectedMonth = null,
  onMonthClick
}) => {
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();
  /* Series picked from the legend, as on the full-year chart: empty shows all
     three, and clicking one narrows the chart to it (click more to add, again
     to drop). */
  const [pickedSeries, setPickedSeries] = useState<SeriesKey[]>([]);
  const shownSeries = pickedSeries.length ? SERIES.filter((s) => pickedSeries.includes(s.key)) : SERIES;

  const togglePickedSeries = (key: SeriesKey) =>
    setPickedSeries((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );

  return (
    <ChartPanel>
      <ChartTitle>{title}</ChartTitle>
      <Legend>
        {SERIES.map((s) => (
          <LegendButton
            key={s.key}
            type="button"
            aria-pressed={pickedSeries.includes(s.key)}
            $dimmed={pickedSeries.length > 0 && !pickedSeries.includes(s.key)}
            onClick={() => togglePickedSeries(s.key)}
          >
            <i style={{ background: s.color }} />
            {s.label}
          </LegendButton>
        ))}
      </Legend>
      <ChartPlot>
        <ResponsiveContainer width="100%" height={220} minWidth={360}>
          <BarChart
            data={data}
            margin={{ top: 22, right: 12, left: 0, bottom: 4 }}
            accessibilityLayer={false}
            /* Anywhere in a month's column selects it, as in Looker Studio --
               not only its bars, which can be short or missing. */
            onClick={(state) => {
              const index = Number(state?.activeIndex);
              const month = Number.isInteger(index) ? data[index] : undefined;
              if (month && onMonthClick) onMonthClick(month.monthKey);
            }}
            style={onMonthClick ? { cursor: 'pointer' } : undefined}
          >
            <CartesianGrid stroke={shell.chartGrid} vertical={false} />
            <XAxis
              dataKey="axisLabel"
              tick={{ fill: shell.dataText, fontSize: 10, fontWeight: 400 }}
              axisLine={{ stroke: shell.chartAxisLine }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              {...currencyAxisProps(maxOf(data, shownSeries.map((s) => s.key)))}
              width={52}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                SERIES.find((s) => s.key === name)?.label ?? name
              ]}
              contentStyle={{
                background: '#fff',
                borderRadius: 4,
                fontSize: 12
              }}
            />
            {/* Every series stays mounted and the unpicked ones are hidden:
                removing a Bar and adding it back moved it to the front. */}
            {SERIES.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.key}
                fill={s.color}
                radius={[2, 2, 0, 0]}
                maxBarSize={40}
                hide={!shownSeries.includes(s)}
              >
                {data.map((month) => (
                  <Cell
                    key={month.monthKey}
                    fillOpacity={selectedMonth && month.monthKey !== selectedMonth ? DIMMED_OPACITY : 1}
                  />
                ))}
                <LabelList
                  dataKey={s.key}
                  position="top"
                  formatter={formatCurrencyCompact}
                  fill={shell.dataText}
                  fontSize={10}
                  fontWeight={600}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartPlot>
    </ChartPanel>
  );
};
